import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { keyframes } from "@mui/system";
import CustomAppBar from "../../../components/CustomAppBar";
import ContractWarning from "../components/ContractWarning";
import ContextSummary from "../components/ContextSummary";
import ActionGrid from "../components/ActionGrid";
import { customerService } from "../services/customerService";
import {
  version,
  bgGradient,
  STORAGE_KEYS,
  MESSAGES,
} from "../../../constants/consts";

// --- Animasyonlar ---
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export default function CustomerHome({ user, onLogout }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  const username =
    user?.username ||
    localStorage.getItem(STORAGE_KEYS.USERNAME) ||
    sessionStorage.getItem(STORAGE_KEYS.USERNAME) ||
    "";
  const sunucuKodu =
    user?.sunucu_kodu ||
    localStorage.getItem(STORAGE_KEYS.SUNUCU_KODU) ||
    sessionStorage.getItem(STORAGE_KEYS.SUNUCU_KODU) ||
    "";

  useEffect(() => {
    const fetchCompany = async () => {
      if (!sunucuKodu) {
        setLoading(false);
        return;
      }
      try {
        const data = await customerService.getCompanyBySunucu(sunucuKodu);
        if (Array.isArray(data) && data.length > 0) {
          setContract(data[0].contract);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [sunucuKodu]);

  return (
    <Box
      sx={{
        height: "100dvh", // ← minHeight yerine height
        background: bgGradient,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        "&::before": {
          content: '""',
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          top: -100,
          right: -100,
          animation: `${fadeIn} 1.2s ease-out both`,
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: -80,
          left: -80,
          animation: `${fadeIn} 1.4s ease-out 0.1s both`,
          pointerEvents: "none",
        },
      }}
    >
      {/* CustomAppBar — fixed pozisyonlu, dışarıda */}
      <CustomAppBar
        title={MESSAGES.HOME_TITLE}
        subtitle={MESSAGES.HOME_SUBTITLE}
        isNotHome={false}
        onLogout={onLogout}
      />

      {/* Kaydırılabilir ana içerik */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch", // iOS yumuşak scroll
          pt: { xs: "96px", sm: "112px" }, // CustomAppBar payı
          pb: 3,
          px: 2,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.25)",
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        {/* İçerik ortalanmış, max genişlik */}
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: "100%", // versiyon dibe insin diye
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : (
            <>
              {contract === "no" && (
                <Box
                  sx={{
                    my: 2,
                    animation: `${fadeInUp} 0.5s ease-out 0.2s backwards`,
                  }}
                >
                  <ContractWarning />
                </Box>
              )}

              <Box sx={{ my: 3 }}>
                <ContextSummary username={username} sunucuKodu={sunucuKodu} />
              </Box>

              <Box sx={{ my: 3 }}>
                <ActionGrid />
              </Box>
            </>
          )}

          {/* Versiyon — flex-grow ile dibe itilir, mt:auto YOK */}
          <Box
            sx={{
              mt: "auto", // ← iç sarmalayıcıda flex column + minHeight:100% olduğu için güvenli
              pt: 4,
              display: "flex",
              justifyContent: "center",
              animation: `${fadeIn} 0.8s ease-out 0.55s backwards`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.75)",
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              {version}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
